import { CustomerDiscounts } from '../services/discounts';
import { $Q, $Qll } from '../utils/querySelector';

/**
 * @author Michael Ballen <michael.ballen@gradiweb.com>
 * All functions for discounts customer groups
 */
class Discounts {
  /**
   * Assign discount component
   * @author Michael Ballen <michael.ballen@gradiweb.com>
   */
  constructor(formQuery, cartFormQuery, listQuery) {
    this.form = $Q(formQuery);
    this.cartForm = $Q(cartFormQuery);
    this.listQuery = $Qll(listQuery);
    this.listProduct = $Q(listQuery);
    this.discountPercentage = 0;

    if (this.form != null) {
      this.printPriceProductPage();
    }

    if (this.cartForm != null) {
      this._applyDiscountCode();
    }

    if (this.listQuery[0] != null) {
      this._applyDiscountCodeToList();
      this._applyDiscountCodeToInfiniteScroll();
    }
  }

  /**
   * Get discount for API and show discounted price
   * @author Michael Ballen <michael.ballen@gradiweb.com>
   */
  async printPriceProductPage() {
    const customerDiscounts = new CustomerDiscounts();
    const variantId = $Q('[name="id"]', this.form).value;
    const customDiscountContainer = $Q('.custom-discount');
    const discountCode = $Q('[name="discout_codes"]', this.form);

    if (discountCode) {
      const price = await customerDiscounts.getDiscountedPrice(
        variantId,
        discountCode.value
      );

      const priceDiscounted = $Q(
        '.custom__discount-price',
        customDiscountContainer
      );

      if (price != undefined) {
        customDiscountContainer?.classList.remove('hidden');
        const jsProductRegularPrice = $Q('#js-product-price .regular');
        if (jsProductRegularPrice) {
          jsProductRegularPrice.classList.add('isCustomDiscount');

          priceDiscounted.textContent = this.getPriceWithCurrencySign(`${price.toFixed(2)}`);

          const priceStickyDiscounted = $Q('.stickyaddcart .stickyaddcart__product-price, .navigation-bar-sticky .atc-component__product-prices');

          if (priceStickyDiscounted) {
            const regularPriceStickyDiscounted = $Q('.regular', priceStickyDiscounted);
            if (regularPriceStickyDiscounted) {
              regularPriceStickyDiscounted.innerText = priceDiscounted.innerText;
            }
          }
        }
      }
    }
  }

  /**
   * apply discount code in input app Discounts combine
   * @author Michael Ballen <michael.ballen@gradiweb.com>
   */
  _applyDiscountCode() {
    const discountContainer = $Q('.tbtDiscountCombine', this.cartForm);
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((element) => {
          if (element.classList.contains('dcbInputGroup')) {
            const discountInput = $Q('.dcbInput', this.cartForm);
            const discountButton = $Q('.dcbButton', this.cartForm);
            const discountCodecart = $Q(
              '[name="discout_codes"]',
              this.cartForm
            ).value;

            if (discountCodecart.length === 0) {
              discountInput.value = '';
            } else {
              discountInput.value = `${discountCodecart}`;
              discountButton.click();
            }
          }
        });
      });
    });

    const config = { attributes: false, childList: true, characterData: false };

    observer.observe(discountContainer, config);
  }

  /**
  * Apply discount codes in list product
  * @author Sebastián García <sebastian.garcia@gradiweb.com>
  */
  async _applyDiscountCodeToList() {
    const customerDiscounts = new CustomerDiscounts();
    const code = this.listProduct.dataset.discountCode;
    if (!code) {
      return;
    }
    try {
      const response = await this.discountPriceFromCode(code, customerDiscounts);
      this.discountPercentage = response.percentage;
      this._addDiscountCodeToList();

    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Promise for get discounted percentage by code
   * @author Sebastián García<sebastian.garcia@gradiweb.com>
   * @team Support
   */
  async discountPriceFromCode(code, customerDiscounts) {
    return new Promise((resolve, reject) => {
      customerDiscounts.getCustomerDiscountPercent(code)
        .then(response => {
          resolve({
            percentage: response.data.percentage
          });
        })
        .catch(error => {
          console.error(error);
          reject(error);
        });
    });
  }

  /**
   * Add new product price with discount for customers
   * @author Sebastián García<sebastian.garcia@gradiweb.com>
   * @team Support
   */
  _addDiscountCodeToList() {
    for (let index = 0; index < this.listQuery.length; index++) {
      const productCards = $Qll('.product-card', this.listQuery[index]);
      productCards.forEach(productCard => {
        const regularPrice = $Q('.regular', productCard);
        const discountPrice = $Q('.custom__discount-price', productCard);
        const user = ShopifyAnalytics.meta.page.customerId;
        if (productCard && regularPrice && discountPrice.innerText == '' && user != undefined) {
          const regularPriceValue = regularPrice?.dataset.price / 100;
          const discountPriceValue = this.getPriceWithDiscount(regularPriceValue, this.discountPercentage);
          const formatDiscountPrice = this.getPriceWithCurrencySign(`${discountPriceValue}`);
          regularPrice.classList.add("cancelled");
          discountPrice.innerText = formatDiscountPrice;
        }
      });
    }
  }

  /**
   * Calcule new price according to percentage discount
   * @author Sebastián García<sebastian.garcia@gradiweb.com>
   * @team Support
   */
  getPriceWithDiscount(price, percentage) {
    const multiplier = (100 + parseFloat(percentage)) / 100;
    return (price * multiplier).toFixed(2);
  }

  /**
  * apply discount codes in list product with infiniteScroll
  * @author Sebastián García <sebastian.garcia@gradiweb.com>
  */
  async _applyDiscountCodeToInfiniteScroll() {
    const element = $Q('#js-infinite-scroll');
    if (element) {
      const observer = new MutationObserver((mutationList) => {
        mutationList.forEach((mutation) => {
          this._addDiscountCodeToList();
        })
      });
      const observerOptions = { attributes: true, childList: true, characterData: true };
      observer.observe(element, observerOptions);
    }
  }

  /**
  * Apply discount code a card product
  * @author Sebastián García <sebastian.garcia@gradiweb.com>
  */
  async _applyDiscountCodeToCard(customerDiscounts, productCards, index) {
    for (const productCard of productCards) {
      const discountPrice = $Qll('.custom__discount-price', productCard)[0];
      const regularPrice = $Qll('.regular', productCard)[0];
      if (regularPrice && !regularPrice.classList.contains("cancelled")) {
        const variantId = discountPrice
          ? discountPrice.getAttribute("data-variant-id")
          : false;
        const productId = discountPrice
          ? discountPrice.getAttribute("data-product-id")
          : false;
        const code = this.listQuery[index].getAttribute("data-discount-code");
        const countryCode = this.getCountryCode();
        if (variantId && code && productId) {
          const priceResponse = await customerDiscounts.getDiscountedPriceByCurrency(
            productId,
            variantId,
            countryCode,
            code
          );
          if (priceResponse != undefined) {
            const price = parseFloat(priceResponse);
            if (!isNaN(price)) {
              discountPrice.innerText = this.getPriceWithCurrencySign(`${price.toFixed(2)}`);
              regularPrice.classList.add("cancelled")
            }
          }
        }
      }
    }
  }

  _ghostcode() {
    const checkbox = $Q('#agree');

    checkbox.addEventListener('click', () => {
      const codeList = $Qll('.dcCode');

      codeList.forEach((element) => {
        if (element.firstChild.data != 'discount_applied') {
          const discountInput = $Q('.dcbInput', this.cartForm);
          const discountButton = $Q('.dcbButton', this.cartForm);

          discountInput.value = 'discount_applied';
          discountButton.click();
        }
      });
    });
  }

  getPriceWithCurrencySign(price) {
    const currencyCode = window.ShopifyAnalytics?.meta?.currency;

    if (currencyCode === 'GBP') {
      return "£" + price;
    }
    if (currencyCode === 'PLN') {
      return price + ' zł';
    }
    return "€" + price;
  }

  getCountryCode() {
    const languajeFromUrl = window.location.href.split("/")[3];
    const regex = /ricohgr.eu\/\w\w-gb/;
    if (window.location.href.match(regex)) {
      return "GB";
    }
    else if (languajeFromUrl === "fr") {
      return "FR"
    }
    else if (languajeFromUrl === "de") {
      return "DE"
    }
    else if (languajeFromUrl === "nl") {
      return "NL"
    }
    return "FR";
  }
}

export { Discounts };
